import { v7 as uuidv7 } from "uuid";
import type { OrganizationSignupClient } from "@ps-bridge/clients/organizationSignup";
import type {
    OrganizationSignupError,
    OrganizationSignupRequest,
} from "@ps-bridge/models/clients/organizationSignup";
import type { OptionTs, TaskEitherTs } from "@ps-bridge/interop";
import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import * as Option from "@utils/Option";
import {
    taskEitherChain,
    taskEitherLeft,
    taskEitherOf,
    taskEitherTryCatch,
} from "@utils/effects/branded";
import API_URL from "@utils/config";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import type { HttpStatusCode } from "@ps-bridge/models/brands/httpStatusCode";
import { makeHttpStatusCode } from "@ps-bridge/models/brands/httpStatusCode";

const SIGNUP_ROUTE = "/v1/auth/signup";

type HttpStatusCodeOption = OptionTs<HttpStatusCode>;

type SquareBillingCard = {
    readonly sourceId: string;
    readonly verificationToken: OptionTs<string>;
};

type SquareBillingContact = {
    readonly givenName: string;
    readonly familyName: string;
    readonly email: string;
    readonly phone: string;
    readonly addressLine1: string;
    readonly addressLine2: OptionTs<string>;
    readonly city: string;
    readonly region: string;
    readonly country: string;
    readonly postalCode: string;
};

type SquareBilling = {
    readonly card: SquareBillingCard;
    readonly billingContact: SquareBillingContact;
};

const errorFromStatus = (status: number, message: string): OrganizationSignupError => {
    const cleaned = message.trim();
    const fallback = cleaned === "" ? "Request failed" : cleaned;

    return {
        type: status === 409 ? "Conflict" : status === 400 ? "BadRequest" : "ServerError",
        message: makeErrorMessageText(fallback),
        statusCode: Option.some(makeHttpStatusCode(status)) as HttpStatusCodeOption,
    };
};

const networkError = (): OrganizationSignupError => ({
    type: "NetworkError",
    message: makeErrorMessageText("Network error"),
    statusCode: Option.none<number>() as HttpStatusCodeOption,
});

const serializeSquareBilling = (squareBilling: SquareBilling) => ({
    card: {
        sourceId: squareBilling.card.sourceId,
        ...Option.fold(squareBilling.card.verificationToken, () => ({}), (verificationToken) => ({
            verificationToken,
        })),
    },
    billingContact: {
        givenName: squareBilling.billingContact.givenName,
        familyName: squareBilling.billingContact.familyName,
        email: squareBilling.billingContact.email,
        phone: squareBilling.billingContact.phone,
        addressLine1: squareBilling.billingContact.addressLine1,
        ...Option.fold(squareBilling.billingContact.addressLine2, () => ({}), (addressLine2) => ({
            addressLine2,
        })),
        city: squareBilling.billingContact.city,
        region: squareBilling.billingContact.region,
        country: squareBilling.billingContact.country,
        postalCode: squareBilling.billingContact.postalCode,
    },
});

const serializeRequest = (request: OrganizationSignupRequest) => ({
    email: request.email,
    password: request.password,
    firstName: request.firstName,
    lastName: request.lastName,
    organizationName: request.organizationName,
    planTier: request.planTier,
    goals: request.goals,
    ...Option.fold(request.squareBilling, () => ({}), (squareBilling) => ({
        squareBilling: serializeSquareBilling(squareBilling),
    })),
});

export class DefaultOrganizationSignupClient implements OrganizationSignupClient {
    signup(request: OrganizationSignupRequest): TaskEitherTs<OrganizationSignupError, Ok> {
        const fetchTask = taskEitherTryCatch(
            () =>
                fetch(`${API_URL}${SIGNUP_ROUTE}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Trace-ID": uuidv7(),
                    },
                    credentials: "include",
                    body: JSON.stringify(serializeRequest(request)),
                }),
            () => networkError(),
        );

        return taskEitherChain(fetchTask, (response) => {
            if (response.ok) {
                return taskEitherOf(ok);
            }

            const messageTask = taskEitherTryCatch(
                () => response.text(),
                () => errorFromStatus(response.status, "Response body unavailable"),
            );

            return taskEitherChain(messageTask, (message) =>
                taskEitherLeft(errorFromStatus(response.status, message || response.statusText || "Request failed")),
            );
        });
    }
}
